import type { AuthResponse } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

interface PendingRequest {
  resolve: (response: Response | PromiseLike<Response>) => void
  reject: (error: Error) => void
  endpoint: string
  options: RequestInit
}

let isRefreshing = false
let pendingQueue: PendingRequest[] = []
// Latched once a refresh fails: the session is dead until the user re-logs in.
// Prevents every subsequent 401 from firing another /api/auth/refresh — that
// flood is what drains the per-IP rate-limit budget and blocks re-login.
let sessionDead = false

function emitSessionExpired(): void {
  window.dispatchEvent(new CustomEvent('session-expired'))
}

function emitSessionRestored(): void {
  window.dispatchEvent(new CustomEvent('session-restored'))
}

function saveTokens(data: AuthResponse): void {
  localStorage.setItem('access_token', data.access_token)
  localStorage.setItem('refresh_token', data.refresh_token)
  localStorage.setItem('user', JSON.stringify(data.user))
}

function retryRequest(endpoint: string, options: RequestInit): Promise<Response> {
  const token = localStorage.getItem('access_token')
  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  })
}

function resolvePendingQueue(): void {
  const queue = [...pendingQueue]
  pendingQueue = []
  queue.forEach(({ resolve, endpoint, options }) => {
    resolve(retryRequest(endpoint, options))
  })
}

function rejectPendingQueue(error: Error): void {
  const queue = [...pendingQueue]
  pendingQueue = []
  queue.forEach(({ reject }) => reject(error))
}

async function attemptRefresh(): Promise<AuthResponse> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  const response = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    throw new Error('Refresh failed')
  }

  return response.json()
}

export async function handle401(endpoint: string, options: RequestInit): Promise<Response> {
  // Session already known dead — don't attempt another refresh, just re-signal.
  if (sessionDead) {
    emitSessionExpired()
    throw new Error('Session expired')
  }

  if (isRefreshing) {
    return new Promise<Response>((resolve, reject) => {
      pendingQueue.push({ resolve, reject, endpoint, options })
    })
  }

  isRefreshing = true

  try {
    const data = await attemptRefresh()
    saveTokens(data)
    resolvePendingQueue()
    return retryRequest(endpoint, options)
  } catch {
    sessionDead = true
    rejectPendingQueue(new Error('Session expired'))
    emitSessionExpired()
    throw new Error('Session expired')
  } finally {
    isRefreshing = false
  }
}

export function onReLoginSuccess(data: AuthResponse): void {
  saveTokens(data)
  sessionDead = false
  resolvePendingQueue()
  isRefreshing = false
  emitSessionRestored()
}

/** Only for tests — resets internal singleton state */
export function _resetForTests(): void {
  isRefreshing = false
  sessionDead = false
  pendingQueue = []
}
